import React, { FC, useCallback } from "react";
import { useLocation } from "react-router-dom";
import styled from "styled-components";

import HELP_SRC from "../../Icons/Help.svg";
import { useStoreState } from "../../store";
import { canReachScreen, screenForPath } from "../../utility/routes";
import {
  isSupportAssistantEnabled,
  shouldShowScreenHelp,
} from "../../utility/supportAssistantAccess";
import { isSupportAssistantMounted } from "../../utility/supportAssistantBus";
import { startWalkthrough } from "../../utility/walkthroughRunner";
import { walkthroughFor } from "../../utility/walkthroughs";

/**
 * THE "?" — one per screen, and pressing it WALKS YOU AROUND THAT SCREEN.
 *
 * This is the other half of "help is always where you are", and it is deliberately NOT the
 * chat launcher (`SupportLauncherButton`, the floating bubble bottom-right). The launcher invites a
 * conversation about anything, from anywhere; the "?" is about the screen in front of you, and
 * shows it rather than describing it. `shouldShowScreenHelp` is a separate predicate from
 * `shouldShowSupportLauncher` for that reason, with its own truth table and its own tests. The
 * two now agree on every screen in the app — both are there — but they are answering different
 * questions and only this one is allowed to say no: the "?" depends on the path mapping to a
 * screen, on that screen having a tour, and on this user being able to reach it, none of which
 * is any of the launcher's business.
 *
 * ONE CLICK, AND THE TOUR IS RUNNING
 * ----------------------------------
 * It used to pre-fill the chat composer with that screen's question and leave the user to
 * press Send. Two things were wrong with that. It was three actions (press "?", read the
 * question, press Send) to reach an answer they had already asked for by pressing the only
 * help control on the screen; and what it produced was PROSE ABOUT the screen, next to the
 * screen, when the thing that was actually wanted is the screen itself with the control
 * highlighted. `startWalkthrough` goes straight to the second one. There is no model call, no
 * network, and nothing to read first.
 *
 * The walkthrough is also the safer failure: it points at real controls the user presses
 * themselves (`WalkthroughOverlay`), so a tour that has gone stale against the UI says so by
 * skipping a step, whereas a stale ANSWER reads as authoritative.
 *
 * WHY THE SCREEN IS RESOLVED FROM THE PATH
 * ----------------------------------------
 * `screenForPath` is the single matcher, so the "?" can never name a screen the rest of the
 * assistant does not know about, and can never appear on a path the screen table deliberately
 * omits (`/login`, `/logout`, the terms pages, `/diagnostic`). `canReachScreen` is then the
 * single gate, so the "?" cannot offer a tour of a screen the user has no business on — in
 * practice the router would already have bounced them, which is what makes this a second line
 * rather than the only one. A walkthrough key IS a screen key, so both questions are asked of
 * the same value and there is no second namespace to keep in step.
 */

/**
 * WHERE IT SITS: IN THE HEADER, WITH THE OTHER UTILITY CONTROLS. IT DOES NOT FLOAT.
 *
 * It used to be `position: fixed` in the bottom-right corner, stepping sideways when the chat
 * launcher was also on screen so the two would not overlap. That is gone — and so, a day later,
 * is the floating launcher it was stepping around. Help now lives in exactly one place on every
 * screen — next to Images, Documents, the account menu, the gear, the launcher and the About
 * icon — rather than hovering over whatever the user is working on, and the "which corner is
 * free today" arithmetic (and with it this file's only reason to know the launcher's rule) goes
 * with it. `CSMShopfloorHeader` decides the position now; this is an ordinary inline button that
 * sits where its parent puts it.
 *
 * ⚠️ THE APPEARANCE IS THE HEADER'S, NOT THIS FILE'S. `utility-link` + `user-icon` are the
 * header's own classes (`CSMShopfloorHeader.css`), and they carry the 16px icon box, the 4px
 * icon/label gap, the Swagelok-blue recolour filter at 50% opacity, the full-opacity hover and
 * the phone rules in `styles/responsive.css` that hide labels and raise the touch target. Using
 * them rather than restating the values is what keeps this control in step with its neighbours
 * when the header is restyled, and it is why the icon's own `#004D9E` fill is not what you see:
 * like every other header icon it is flattened and recoloured by that filter. All this
 * styled-component adds is the reset a native `<button>` needs to stop looking like a form
 * control; a `<button>` rather than the `div role="button"` the older header controls use,
 * because this is a real action with a real accessible name.
 */
const Button = styled.button`
  background: none;
  border: 0;
  padding: 0;
  margin: 0;
  color: inherit;
  font: inherit;
  cursor: pointer;
`;

const ScreenHelpButton: FC = () => {
  const location = useLocation();

  const user = useStoreState((state: any) => state.user);

  // `/home` is three screens behind one path (`Home.tsx`), so the path alone does not say which
  // tour this "?" offers — the view flags do. They are read here as well as in the overlay
  // because this button's NAME is the tour's label: without them the button on the Active jobs
  // screen announced itself as a tour of the shop-floor board, and started one.
  const isManagerMode = useStoreState((state: any) => state.appSettings?.isManagerMode ?? false);
  const isActiveJobMode = useStoreState(
    (state: any) => state.appSettings?.isActiveJobMode ?? true
  );

  const screen = screenForPath(location.pathname);

  // The tour this screen has, or null. Resolved BEFORE the render decision, not inside the
  // click handler — see the gate below.
  const walkthrough = walkthroughFor(screen?.key, { isManagerMode, isActiveJobMode });

  const showMe = useCallback(() => {
    if (!walkthrough) return;
    // The overlay is mounted once at the app root and is not an ancestor of this button, so
    // this goes through the module-level bus. A request that arrives before the overlay has
    // subscribed is held (`walkthroughRunner`'s queue of one) rather than dropped — "the
    // button did nothing the first time" is the worst failure a help control has.
    //
    // `scan: true` — the tour is built from the controls actually on screen and described in
    // the card by CoSMo (curated wording first). The overlay falls back to the curated steps
    // when the scan finds nothing.
    startWalkthrough(walkthrough.key, { scan: true });
  }, [walkthrough]);

  // The flag decides whether the feature exists at all, and is checked first and separately —
  // an environment without the secret renders nothing whatever the predicate below says.
  if (!isSupportAssistantEnabled()) return null;

  // A "?" that opens nothing is strictly worse than no "?": the user concludes the app is
  // broken rather than that the help simply is not there.
  //
  // ⚠️ THIS ASKS ABOUT THE PANEL AND IT IS STILL THE RIGHT QUESTION, even though the "?" no
  // longer opens the panel. `SupportAssistantHost` renders the panel and the overlay in one
  // commit and suppresses both on the same routes, so "the panel is listening" is the only
  // published fact that means "the host is mounted". The alternative — a second mounted-check
  // on `walkthroughRunner` — would be a second answer to keep true, for a case that cannot
  // arise while the two live in one host.
  //
  // It matters MORE now than it did, not less: this button is rendered by the header and is no
  // longer a sibling of the overlay it drives, so nothing else in the tree guarantees the two
  // were mounted together.
  if (!isSupportAssistantMounted()) return null;

  // ⚠️ AND THE SCREEN MUST ACTUALLY HAVE A TOUR. Every one of the 29 screens does today, and
  // `walkthroughs.test.ts` fails the build when a screen is added without one — so this is a
  // guard against that test being relaxed rather than a live case. It is here because the only
  // thing this button can do now is run a walkthrough: with no walkthrough there is nothing
  // for a press to produce, and the same argument as the mounted check applies.
  if (
    !shouldShowScreenHelp({
      hasScreen: Boolean(screen && walkthrough),
      userCanReachScreen: canReachScreen(screen, { user }),
    }) ||
    !walkthrough
  ) {
    return null;
  }

  return (
    <Button
      type="button"
      className="utility-link"
      // The accessible name says what it is about, not just "?". A screen reader announcing
      // "question mark button" on every screen in the app is a control nobody can use. The
      // wording is the walkthrough's own label, so the button and the tour it starts cannot
      // describe two different things.
      //
      // It is also the only name this control has now that it is icon-only — see the header for
      // why a label beside it is not an option.
      aria-label={walkthrough.label}
      title={walkthrough.label}
      data-testid="screen-help-button"
      onClick={showMe}
    >
      {/* `alt=""` on purpose: the button carries the name, and an alt text here would have a
          screen reader announce the same thing twice. Never an `e-icons` glyph — every one of
          them renders blank app-wide. */}
      <img className="user-icon" alt="" src={HELP_SRC} />
    </Button>
  );
};

export default ScreenHelpButton;
